import type { BaseEntityState, ServerListParams } from '@crm/shared';

// Fonte di verità dei types Quotation: vivono in quotationMappers, accanto al
// DTO REST e ai mapper int<->string. Qui li re-esportiamo per dare ai
// componenti UI un solo import path, come fanno le altre entità del core.
export type {
  Quotation,
  CoreQuotationStatus,
  QuotationStatus,
  QuotationDto,
  QuotationInput,
} from './quotationMappers';

import type { Quotation, CoreQuotationStatus, QuotationStatus } from './quotationMappers';

// Costanti UI con chiave i18n. Il tipo è `CoreQuotationStatus`, non l'unione
// aperta `QuotationStatus`: questa lista è il vocabolario del PRODOTTO, e
// tipizzarla sul sottoinsieme chiuso è ciò che fa emergere a compile-time una
// voce dimenticata o scritta male. Gli stati contribuiti dai plugin non stanno
// qui — arrivano dal `vocabularyRegistry`, con la propria chiave i18n.
//
// SYNC CROSS-LAYER — aggiunte, rimozioni e rinomini vanno replicati su:
//   QUOTATION_STATUSES -> quotationMappers.ts (CoreQuotationStatus + QUOTATION_STATUS_TO_INT),
//                         modules/crm-util/.../constants/QuotationStatus.java,
//                         locales/{it,en}.json (quotation.statuses.*)

export const QUOTATION_STATUSES: { value: CoreQuotationStatus; label: string }[] = [
  { value: 'draft', label: 'quotation.statuses.draft' },
  { value: 'sent', label: 'quotation.statuses.sent' },
  { value: 'accepted', label: 'quotation.statuses.accepted' },
  { value: 'rejected', label: 'quotation.statuses.rejected' },
  { value: 'expired', label: 'quotation.statuses.expired' },
  { value: 'cancelled', label: 'quotation.statuses.cancelled' },
];

// Stato preventivo -> variante del chip del design system (`.chip` in
// _list.scss). Unica fonte di verità: la usano sia la lista sia la card di
// relazione, che prima avevano due mappe divergenti.
//
// Gli stati "vivi" prendono il colore dal BRAND, così cambiare i due colori
// dell'azienda (primary/secondary in BrandPalette) ricolora anche i chip senza
// toccare il CSS: `soft` deriva da primary, `gold` da secondary.
//
// I due stati senza colore proprio restano grigi ma NON uguali: bozza è in
// lavorazione (`neutral`, grigio pieno), annullato è archiviato (`ghost`, solo
// contorno e testo più chiaro), così si distinguono a colpo d'occhio in lista.
//
// Rifiutato e scaduto restano sui semantici fissi (crit/warn): la palette li
// tiene deliberatamente fuori dal brand per non perdere la leggibilità di uno
// stato negativo con clienti dal primario rosso o arancio.
export const QUOTATION_STATUS_CHIP: Record<string, string> = {
  draft: 'neutral',
  sent: 'soft',
  accepted: 'gold',
  rejected: 'crit',
  expired: 'warn',
  cancelled: 'ghost',
};

/** Variante chip per uno stato, con ripiego neutro per gli stati dei plugin. */
export function quotationStatusChipClass(status?: string | null): string {
  return (status && QUOTATION_STATUS_CHIP[status]) || 'neutral';
}

export interface QuotationState extends BaseEntityState<Quotation> {
  /** Totale record disponibili lato server, per la paginazione della DataGrid. */
  totalCount: number;
}

export interface QuotationCreatePayload {
  externalCode?: string | null;
  businessId: number;
  /**
   * Denormalizzato UI-only: non finisce nel DTO wire (vedi
   * `quotationCreateToInput`). Utile a chi costruisce il payload di un update
   * inline passando anche il nuovo nome scelto da lookup, così Redux resta
   * consistente senza refetch.
   */
  businessName?: string;
  title: string;
  dueDate?: string | null;
  amount?: number;
  notes?: string | null;
  /**
   * UI-only in creazione: il DTO wire non lo include, il backend inizializza
   * a DRAFT. In update viene inviato e vale come transizione di stato.
   */
  status?: QuotationStatus;
  /**
   * Stato attuale della pipeline (FK). TS-101: valorizzato dalla combo a cascata
   * pipeline->stato del form, sia in creazione sia in update. Obbligatorio solo
   * se esiste ≥1 pipeline (guard nel form + controllo nel resource backend);
   * senza pipeline resta 0. È anche il canale del drag-to-move sulla board.
   */
  currentStateId?: number;
}

export interface QuotationUpdatePayload extends Partial<QuotationCreatePayload> {
  quotationId: number;
}

/** Parametri di listing per `GET /quotations/{groupId}`. Tutti opzionali. */
export interface QuotationListParams extends ServerListParams {
  /**
   * Picker legacy (TS-94): se valorizzato, restituisce i preventivi già in quella
   * pipeline o non associati ad alcuna pipeline (currentStateId=0).
   */
  pipelineId?: number;
  /**
   * Board della kanban (TS-101): se valorizzato, restituisce i preventivi della board
   * indicata risolvendone il filtro lato server. Ha precedenza su `pipelineId`.
   */
  kanbanId?: number;
}
